import React, { useState, useEffect } from 'react';
import { User, Phone, Calendar, MapPin, Target, LogOut, Edit2, ChevronDown, ChevronUp, Bell, BellOff } from 'lucide-react';
import { useMemberPortal } from './MemberPortal';
import { useAuth } from '../../context/AuthContext';
import { KeyRound } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { submitProfileEditRequest, getMyProfileEditRequests } from '../../lib/memberDb';
import { isPushSupported, getNotificationPermission, getCurrentSubscription, requestAndSubscribe, unsubscribeFromPush } from '../../lib/pushNotifications';

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
        <p className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">{value}</p>
      </div>
    </div>
  );
};

const statusConfig = {
  pending: { label: '검토 중', color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' },
  approved: { label: '승인됨', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' },
  rejected: { label: '거절됨', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' },
};

const fieldLabels = {
  phone: '전화번호',
  address: '주소',
  purpose: '운동 목적',
};

export default function MyInfo() {
  const { memberRecord } = useMemberPortal();
  const { signOut, user, updatePassword } = useAuth();
  const [showPwForm, setShowPwForm] = useState(false);
  const [newPw, setNewPw] = useState('');

  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (pushSupported) {
      getCurrentSubscription().then(sub => setPushSubscribed(!!sub));
    }
  }, [pushSupported]);
  const [newPwConfirm, setNewPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);

  async function handlePasswordChange(e) {
    e.preventDefault();
    setPwError('');
    if (newPw !== newPwConfirm) { setPwError('비밀번호가 일치하지 않습니다.'); return; }
    if (newPw.length < 6) { setPwError('비밀번호는 6자 이상이어야 합니다.'); return; }
    setPwLoading(true);
    const { error } = await updatePassword(newPw);
    setPwLoading(false);
    if (error) { setPwError('비밀번호 변경에 실패했습니다.'); return; }
    setPwSuccess(true);
    setNewPw(''); setNewPwConfirm('');
    setTimeout(() => { setPwSuccess(false); setShowPwForm(false); }, 2000);
  }
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({ phone: '', address: '', purpose: '' });
  const [editNote, setEditNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    getMyProfileEditRequests(memberRecord.id).then(setEditHistory);
  }, [memberRecord.id]);

  function openEditForm() {
    setEditData({
      phone: memberRecord.phone || '',
      address: memberRecord.address || '',
      purpose: memberRecord.purpose || '',
    });
    setEditNote('');
    setShowEditForm(true);
  }

  async function handleSubmitEdit(e) {
    e.preventDefault();
    const changes = {};
    Object.entries(editData).forEach(([field, after]) => {
      const before = memberRecord[field] || '';
      if (after !== before) {
        changes[field] = { before, after };
      }
    });
    if (Object.keys(changes).length === 0) {
      setShowEditForm(false);
      return;
    }
    setSubmitting(true);
    const { error } = await submitProfileEditRequest(memberRecord.id, { changes, note: editNote });
    setSubmitting(false);
    if (!error) {
      setShowEditForm(false);
      const updated = await getMyProfileEditRequests(memberRecord.id);
      setEditHistory(updated);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">내 정보</h2>

      {/* 프로필 카드 */}
      <div className="card p-5">
        <div className="flex items-center gap-4 mb-4">
          {memberRecord.photo ? (
            <img src={memberRecord.photo} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <User className="w-8 h-8 text-primary-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 dark:text-white text-lg">{memberRecord.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {memberRecord.gender === 'male' ? '남성' : memberRecord.gender === 'female' ? '여성' : ''}
            </p>
          </div>
          <button
            onClick={openEditForm}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" /> 수정 요청
          </button>
        </div>

        <div>
          <InfoRow icon={Phone} label="전화번호" value={memberRecord.phone} />
          <InfoRow
            icon={Calendar}
            label="생년월일"
            value={memberRecord.birthDate ? format(parseISO(memberRecord.birthDate), 'yyyy년 M월 d일') : null}
          />
          <InfoRow icon={MapPin} label="주소" value={memberRecord.address} />
          <InfoRow icon={Target} label="운동 목적" value={memberRecord.purpose} />
        </div>
      </div>

      {/* 정보 수정 요청 폼 */}
      {showEditForm && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">정보 수정 요청</h3>
          <form onSubmit={handleSubmitEdit} className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">전화번호</label>
              <input
                type="tel"
                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={editData.phone}
                onChange={e => setEditData(d => ({ ...d, phone: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">주소</label>
              <input
                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={editData.address}
                onChange={e => setEditData(d => ({ ...d, address: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">운동 목적</label>
              <select
                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={editData.purpose}
                onChange={e => setEditData(d => ({ ...d, purpose: e.target.value }))}
              >
                <option value="">선택</option>
                {['다이어트','근력증가','체력증진','재활운동','체형교정','기타'].map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">요청 사유 (선택)</label>
              <textarea
                className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                rows={2}
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
                placeholder="수정 이유를 간략히 적어주세요"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
              >
                {submitting ? '신청 중...' : '신청하기'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 수정 신청 내역 */}
      {editHistory.length > 0 && (
        <div className="card overflow-hidden">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <span>수정 신청 내역 ({editHistory.length}건)</span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHistory && (
            <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {editHistory.map(req => {
                const cfg = statusConfig[req.status] || statusConfig.pending;
                return (
                  <div key={req.id} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">
                        {req.createdAt ? format(parseISO(req.createdAt), 'yyyy.MM.dd') : ''}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(req.changes || {}).map(([field, val]) => {
                        const label = fieldLabels[field] || field;
                        const before = typeof val === 'object' && val !== null && 'before' in val ? val.before : '';
                        const after = typeof val === 'object' && val !== null && 'after' in val ? val.after : val;
                        return (
                          <p key={field} className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">{label}:</span>{' '}
                            {before && <span className="line-through text-gray-400">{before}</span>}
                            {before && ' → '}{after}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 계정 정보 */}
      <div className="card p-5 space-y-3">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">계정 정보</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
        {!showPwForm ? (
          <button
            onClick={() => { setShowPwForm(true); setPwError(''); setPwSuccess(false); }}
            className="flex items-center gap-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            <KeyRound className="w-3.5 h-3.5" /> 비밀번호 변경
          </button>
        ) : (
          <form onSubmit={handlePasswordChange} className="space-y-2 pt-1">
            <input
              type="password"
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="새 비밀번호 (6자 이상)"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              required
            />
            <input
              type="password"
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="새 비밀번호 확인"
              value={newPwConfirm}
              onChange={e => setNewPwConfirm(e.target.value)}
              required
            />
            {pwError && <p className="text-xs text-red-500">{pwError}</p>}
            {pwSuccess && <p className="text-xs text-green-600 dark:text-green-400">비밀번호가 변경되었습니다.</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowPwForm(false)} className="flex-1 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400">취소</button>
              <button type="submit" disabled={pwLoading} className="flex-1 py-2 rounded-xl bg-primary-600 text-white text-xs font-medium disabled:opacity-60">
                {pwLoading ? '변경 중...' : '변경'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* 알림 설정 */}
      {pushSupported && (
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
          <div className="flex items-center gap-3">
            {pushSubscribed
              ? <Bell className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              : <BellOff className="w-4 h-4 text-gray-400" />
            }
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {pushSubscribed ? '알림 활성화됨' : '알림 비활성화됨'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">회원권 만료, PT 일정 알림</p>
              {pushPermission === 'denied' && (
                <p className="text-xs text-red-500 mt-0.5">브라우저 설정에서 알림을 허용해주세요.</p>
              )}
            </div>
          </div>
          {pushPermission !== 'denied' && (
            pushSubscribed ? (
              <button
                disabled={pushLoading}
                onClick={async () => {
                  setPushLoading(true);
                  await unsubscribeFromPush(null);
                  setPushSubscribed(false);
                  setPushLoading(false);
                }}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 disabled:opacity-50"
              >
                {pushLoading ? '처리 중...' : '알림 끄기'}
              </button>
            ) : (
              <button
                disabled={pushLoading}
                onClick={async () => {
                  setPushLoading(true);
                  const { error } = await requestAndSubscribe(null, 'member');
                  if (!error) { setPushSubscribed(true); setPushPermission('granted'); }
                  setPushLoading(false);
                }}
                className="text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white disabled:opacity-50"
              >
                {pushLoading ? '처리 중...' : '알림 켜기'}
              </button>
            )
          )}
        </div>
      )}

      {/* 로그아웃 */}
      <button
        onClick={signOut}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        로그아웃
      </button>
    </div>
  );
}
